import React, { useRef, useState, useEffect } from 'react';

export type LandingBackgroundOverlay = 'light' | 'lightStrong' | 'subtle' | 'dark' | 'none';

export interface LandingBackgroundMediaProps {
  /** Primary video URL (e.g. MP4). Looping vineyard footage. */
  videoSrc?: string;
  /** Optional WebM. */
  videoSrcWebm?: string;
  /** Poster shown before load and when reduced-motion is preferred. */
  posterSrc?: string;
  /** Fallback image when video is not used. */
  fallbackSrc?: string;
  /** Alt for poster/fallback (video is decorative when used as background). */
  alt: string;
  /** Overlay so text on top stays readable: light (white tint), dark (dark tint), none. */
  overlay?: LandingBackgroundOverlay;
  /** When true, only load/play when section is in view. */
  lazy?: boolean;
  /** Optional class for the background wrapper. */
  className?: string;
  /** Optional class for the media (video/img) to e.g. blur the background (e.g. "blur-md"). */
  mediaClassName?: string;
}

const overlayStyles: Record<LandingBackgroundOverlay, string> = {
  light: 'bg-white/60',
  lightStrong: 'bg-white/70',
  subtle: 'bg-white/15',
  dark: 'bg-black/50',
  none: '',
};

/**
 * Full-bleed background video (or poster) for landing sections. Renders behind content;
 * use overlay so text and UI on top stay readable.
 */
export const LandingBackgroundMedia: React.FC<LandingBackgroundMediaProps> = ({
  videoSrc,
  videoSrcWebm,
  posterSrc,
  fallbackSrc,
  alt,
  overlay = 'light',
  lazy = false,
  className = '',
  mediaClassName = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(!lazy);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!lazy || !containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setInView(true);
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy]);

  const hasAnySource = Boolean(videoSrc || posterSrc || fallbackSrc);
  if (!hasAnySource) return null;

  const showVideo = Boolean(inView && videoSrc && !prefersReducedMotion);
  const showPoster = posterSrc && !showVideo;
  const showFallback = fallbackSrc && !showVideo && !showPoster;

  const mediaClass = `absolute inset-0 w-full h-full object-cover ${mediaClassName}`.trim();

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 z-0 overflow-hidden ${className}`.trim()}
      aria-hidden
    >
      {showVideo && (
        <video
          className={mediaClass}
          autoPlay
          muted
          loop
          playsInline
          poster={posterSrc}
          aria-hidden
        >
          {videoSrcWebm && <source src={videoSrcWebm} type="video/webm" />}
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
      {showPoster && (
        <img
          src={posterSrc}
          alt=""
          className={mediaClass}
          loading={lazy ? 'lazy' : 'eager'}
          aria-hidden
        />
      )}
      {showFallback && (
        <img
          src={fallbackSrc}
          alt=""
          className={mediaClass}
          loading={lazy ? 'lazy' : 'eager'}
          aria-hidden
        />
      )}
      {overlay !== 'none' && (
        <div
          className={`absolute inset-0 pointer-events-none ${overlayStyles[overlay]}`}
          aria-hidden
        />
      )}
    </div>
  );
};
